import {
  FC, useCallback, useEffect, useRef, useState
} from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import Button from '../Button'
import Modal from '../modal/Modal'
import ModalTitle from '../modal/ModalTitle'
import ModalContent from '../modal/ModalContent'
import { REQUIRED_MESSAGE } from '../../features/login/constants'
import FormTextField from '../formControls/FormTextField'
import { ReactComponent as EmailIcon } from '../../features/login/assets/Email.svg'
import { TForm } from '../../features/login/definitions'
import { useExportResultMutation } from './queries/useExportResultQuery'
import { useNotificationContext } from '../../features/notifications/state/useNotification'
import ErrorNotification from '../../features/notifications/components/ErrorNotification'
import { EMAIL_REGEXP } from '../../features/userManagement/regexp'
import { INVALID_EMAIL_MESSAGE } from '../../features/userManagement/constants'
import { useAuthContext } from '../../../state/useAuth'
import { TExportPayload } from './definitions'
import { getExportErrorMessage } from './utils'

const FormStyled = styled.form`
  display: grid;
  grid-row-gap: 16px;
  width: 100%
`

type TProps = {
  getExportPayload: () => TExportPayload
  disabled?: boolean
}

const ExportButton: FC<TProps> = ({ getExportPayload, disabled }) => {
  const { mutateAsync: exportResult, isLoading } = useExportResultMutation()
  const { showErrorNotification, showSuccessNotification } = useNotificationContext()
  const userEmail = useAuthContext().authData?.email ?? ''
  const defaultEmail = EMAIL_REGEXP.test(userEmail) ? userEmail : ''

  const [isOpen, setIsOpen] = useState(false)
  // `isLoading` updates on the next render, so a quick double submit could still send two exports
  const isSubmittingRef = useRef(false)
  const openCountRef = useRef(0)

  const {
    control,
    formState,
    clearErrors,
    reset,
    handleSubmit
  } = useForm<TForm>({
    defaultValues: {
      email: ''
    },
    mode: 'onSubmit'
  })
  const { errors, isDirty } = formState

  const onSubmit = async ({ email }: TForm) => {
    if (isSubmittingRef.current) {
      return
    }
    isSubmittingRef.current = true
    const openCount = openCountRef.current
    try {
      const response = await exportResult({ ...getExportPayload(), email })
      showSuccessNotification(<Box>{response?.message || `The Excel export has been sent to ${email}`}</Box>)
    } catch (error) {
      showErrorNotification(<ErrorNotification message={getExportErrorMessage(error)} />)
    } finally {
      isSubmittingRef.current = false
      // Close on error too: the modal backdrop is above the notifications and would hide them
      if (openCount === openCountRef.current) {
        setIsOpen(false)
      }
    }
  }

  useEffect(() => {
    if (isDirty && (errors.email || errors.password)) {
      clearErrors()
    }
  }, [clearErrors, errors, isDirty])

  const handleOpenModal = useCallback(() => {
    openCountRef.current += 1
    reset({ email: defaultEmail })
    setIsOpen(true)
  }, [reset, defaultEmail])

  return (
    <>
      <Button onClick={handleOpenModal} disabled={disabled}>
        Export
      </Button>
      <Modal open={isOpen} closeModal={() => setIsOpen(false)} withCloseButton>
        <ModalTitle>
          All results will be exported to Excel and emailed to
        </ModalTitle>
        <ModalContent>
          <FormStyled onSubmit={handleSubmit(onSubmit)}>
            <FormTextField
              id="email"
              label="Enter Email"
              fullWidth
              name="email"
              placeholder="email@domain.com"
              startIcon={<EmailIcon />}
              type="email"
              control={control}
              rules={{
                required: { value: true, message: REQUIRED_MESSAGE },
                pattern: { value: EMAIL_REGEXP, message: INVALID_EMAIL_MESSAGE }
              }}
            />
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                loading={isLoading}
                disabled={isLoading}
                variant="primary"
                type="submit"
              >
                Ok
              </Button>
            </Box>
          </FormStyled>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ExportButton
